import React from 'react';
import { signupStyles } from '@/components/features/registration/signup-templates/signup.styles';
import { Box, Stack, Button, Field, createListCollection, Select, Portal } from '@chakra-ui/react';
import { availabilitySchema, TAvailability } from './availability.validator';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TClinicDetailsProps } from '../../../templates/clinic/types/template.types';

const AvailabilityForm = ({ next, details }: TClinicDetailsProps) => {
   const {
      control,
      handleSubmit,
      reset,
      formState: { errors },
   } = useForm<TAvailability>({
      defaultValues: {
         availability: details.availability,
      },
      resolver: zodResolver(availabilitySchema),
   });

   const onSubmit = handleSubmit(async data => {
      next('privacyPolicy', { availability: data.availability });
   });

   const availabilityOptions = createListCollection({
      items: [
         { label: 'within 24 hours', value: 1 },
         { label: 'within 2-3 days', value: 3 },
         { label: 'within 3-4 days', value: 4 },
      ],
   });

   return (
      <Box as="form" css={signupStyles.signup_form} onSubmit={onSubmit}>
         <Stack gap="4" align="flex-start" minW="full">
            <Field.Root invalid={Boolean(errors.availability?.message)} borderRadius="12px">
               <Field.Label textStyle="xs" color="white" mb="1">
                  Availability
                  <Box as="span" color="error">
                     *
                  </Box>
               </Field.Label>
               <Controller
                  control={control}
                  name="availability"
                  render={({ field }) => (
                     <Select.Root
                        bg="#2D2D2D"
                        borderRadius="12px"
                        name={field.name}
                        value={[field.value.toString()]}
                        onValueChange={({ value }) => field.onChange(value?.[0])}
                        onInteractOutside={() => field.onBlur()}
                        collection={availabilityOptions}
                     >
                        <Select.HiddenSelect />
                        <Select.Control>
                           <Select.Trigger>
                              <Select.ValueText placeholder="Select">
                                 {field.value
                                    ? availabilityOptions.items.find(item => item.value === field.value)?.label
                                    : 'Select'}
                              </Select.ValueText>
                           </Select.Trigger>
                           <Select.IndicatorGroup>
                              <Select.Indicator />
                           </Select.IndicatorGroup>
                        </Select.Control>
                        <Portal>
                           <Select.Positioner>
                              <Select.Content bg="#2D2D2D">
                                 {availabilityOptions.items.map(item => (
                                    <Select.Item
                                       item={item}
                                       key={item.value}
                                       textTransform="capitalize"
                                       color="white"
                                       cursor="pointer"
                                       _hover={{ bg: '#3B3B3B' }}
                                    >
                                       {item.label}
                                       <Select.ItemIndicator />
                                    </Select.Item>
                                 ))}
                              </Select.Content>
                           </Select.Positioner>
                        </Portal>
                     </Select.Root>
                  )}
               />
               <Field.ErrorText>{errors.availability?.message}</Field.ErrorText>
            </Field.Root>
            <Box css={signupStyles.signup_btn_con}>
               <Button
                  type="reset"
                  onClick={() => reset()}
                  bg="darkerBtn"
                  textStyle="xs"
                  color="white"
                  h="10"
                  maxW="36"
                  minW="36"
                  p="0"
               >
                  Reset
               </Button>
               <Button type="submit" textStyle="xs" h="10" maxW="36" minW="36" p="0">
                  Next
               </Button>
            </Box>
         </Stack>
      </Box>
   );
};

export default AvailabilityForm;
